import { ReactNode } from 'react';
import { IoClose } from 'react-icons/io5';
import { RxCross1 } from 'react-icons/rx';

import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const CustomXCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <IoClose
    {...props}
    size={20}
    className="text-[#B0B0B0] rounded-full bg-[#E5E5E58F] p-0.5 mr-1"
  />
);

type AutocompleteDialogProps = {
  open: boolean;
  onClose: () => void;
  questionLabel?: string;
  questionDescription?: string;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  /** Search input placeholder; defaults to 'Rechercher une réponse'. */
  searchPlaceholder?: string;
  /** Hide the results area (the KB autocomplete hides it under 3 chars when hideResponsesByDefault). */
  hideResults?: boolean;
  children: ReactNode;
};

/**
 * The modal chrome shared by every autocomplete-style response: close row, pinned
 * question header, pinned search field with a clear icon, and a scrolling results
 * area. Matching and selection stay with the caller.
 */
export const AutocompleteDialog = ({
  open,
  onClose,
  questionLabel,
  questionDescription,
  searchText,
  onSearchTextChange,
  searchPlaceholder,
  hideResults,
  children,
}: AutocompleteDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      if (!next) onClose();
    }}
  >
    <DialogContent className="h-auto nsm:h-[652px] nsm:w-[390px] flex flex-col overflow-hidden">
      <div className="flex items-start justify-between rounded-t p-2">
        <div />
        <DialogClose
          className="h-6 mt-4 mr-4 cursor-pointer focus:ring-0 focus:outline-hidden focus:border-transparent"
          onClick={onClose}
        >
          <RxCross1 className="h-8 w-8 p-1 hover:bg-gray-200 rounded-lg text-gray-600 stroke-[0.5]" />
        </DialogClose>
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-col gap-6 px-8 pb-4 shrink-0">
          <div className="flex flex-col">
            <span className="font-semibold text-xl">{questionLabel}</span>
            <span className="font-light sm:hidden">{questionDescription}</span>
          </div>

          <div className="relative w-full">
            <Input
              autoFocus
              // The text is matched against names, not prose: a keyboard that
              // rewrites it (Boeuf → Bœuf, an unusual surname → a dictionary
              // word) makes the patient search for something they did not type.
              autoCorrect="off"
              spellCheck={false}
              // The browser's own dropdown would cover the suggestion list.
              autoComplete="off"
              value={searchText}
              placeholder={searchPlaceholder ?? 'Rechercher une réponse'}
              onChange={(e) => onSearchTextChange(e.target.value)}
            />
            {searchText.length > 0 && (
              <div
                onClick={() => onSearchTextChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-50 cursor-pointer"
              >
                <CustomXCircleIcon />
              </div>
            )}
          </div>
        </div>
        <div
          className={cn(
            'flex-1 min-h-0 overflow-y-auto px-8 no-scrollbar',
            hideResults && 'hidden',
          )}
        >
          {children}
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
